import type { CastIdentityEntry } from "@shared/castIdentity";

const DRAFT_KEY = "solmi_video_composer_draft_v1";
const DB_NAME = "solmi_video_composer_draft_files";
const DB_VERSION = 1;
const STORE_NAME = "files";
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

export type VideoComposerPendingFile = {
  key: string;
  name: string;
  type: string;
  size: number;
  lastModified: number;
};

export type VideoComposerDraftSource = {
  id?: string;
  type: "link" | "upload" | "mine";
  label: string;
  icon?: "link" | "upload" | "music";
  meta?: string;
  /** Platform of a "link" source; keeps Suno/demo links exempt from the
   *  copyright confirmation after a draft restore. */
  linkPlatform?: "youtube" | "tiktok" | "suno" | "demo" | "other";
  prompt?: string | null;
  style?: string | null;
  gender?: string | null;
  voiceGender?: string | null;
  vocalGender?: string | null;
  audioUrl?: string;
};

export type VideoComposerDraftReference = {
  imageUrl: string;
  name: string;
  role: "character" | "vibe";
  /** Apparent gender from the reference classifier (character images). */
  appearance?: "male" | "female" | "unknown";
  /** Prompt handle ("image1") for "@image1 …" mentions in the prompt. */
  tag?: string;
  /** Saved character (see ReferencePickerModal.ReferenceImage). */
  savedCharacterProjectId?: string;
  /** "It's someone else" answer (see ReferencePickerModal.ReferenceImage). */
  castIdentity?: CastIdentityEntry;
};

export type VideoComposerDraft = {
  origin: "landing" | "director_home";
  prompt?: string;
  source?: VideoComposerDraftSource | null;
  audioUrl?: string;
  audioFileName?: string;
  title?: string;
  aspectRatio?: string;
  resolution?: string;
  // Whether `resolution` was explicitly picked by the user (vs the
  // plan-derived default). Absent on legacy drafts, which are treated as
  // explicit so an old deliberate 480p choice is never overridden.
  resolutionTouched?: boolean;
  selectedStyle?: string;
  outputType?: "video" | "singing" | "lyrics" | "song" | "dance";
  projectMode?: "studio" | "fast" | "fast_v2" | "dance";
  // Whether `projectMode` was picked by the user. A default may still be
  // replaced by the user's director_default_v1 default once they sign in.
  projectModeTouched?: boolean;
  captionStyle?: string;
  captionConfig?: Record<string, unknown>;
  fastClipStartSec?: number;
  fastDurationSec?: number;
  selectedFormat?: string;
  isPublic?: boolean;
  references?: VideoComposerDraftReference[];
  pendingAudioFile?: VideoComposerPendingFile | null;
  pendingReferenceFiles?: VideoComposerPendingFile[];
  createdAt?: number;
  updatedAt?: number;
};

type PendingFileRecord = VideoComposerPendingFile & {
  file: Blob;
  updatedAt: number;
};

const isBrowser = () => typeof window !== "undefined";

export function hasVideoComposerDraftInput(draft: VideoComposerDraft): boolean {
  return Boolean(
    draft.prompt?.trim() ||
      draft.source?.audioUrl ||
      draft.source?.label ||
      draft.audioUrl?.trim() ||
      draft.audioFileName?.trim() ||
      draft.title?.trim() ||
      draft.references?.length ||
      draft.pendingAudioFile ||
      draft.pendingReferenceFiles?.length,
  );
}

/**
 * The library-song fields a lyric, singing or dance tool page restores with
 * a draft's song: track id, style and lyrics. Only a library ("mine") source
 * has them, and only next to its own audio, so one song's lyrics never
 * caption another file (mobile/lib/songSourceTrack.ts). A link or upload
 * source's `meta` is its platform or file size, not a style.
 */
export function draftSourceSongFields(
  source: VideoComposerDraftSource | null | undefined,
  audioUrl: string,
): { sourceTrackId?: string; songStyle?: string; songLyrics?: string } {
  if (source?.type !== "mine" || !audioUrl || source.audioUrl !== audioUrl) {
    return {};
  }
  return {
    sourceTrackId: source.id || undefined,
    songStyle: source.style || source.meta || undefined,
    songLyrics: source.prompt || undefined,
  };
}

export function saveVideoComposerDraft(draft: VideoComposerDraft): void {
  if (!isBrowser()) return;
  try {
    const now = Date.now();
    const createdAt =
      draft.createdAt ?? loadVideoComposerDraft()?.createdAt ?? now;
    localStorage.setItem(
      DRAFT_KEY,
      JSON.stringify({
        ...draft,
        createdAt,
        updatedAt: now,
      }),
    );
  } catch {
    // Draft persistence is best-effort. The composer still works without it.
  }
}

export function loadVideoComposerDraft(): VideoComposerDraft | null {
  if (!isBrowser()) return null;
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as VideoComposerDraft;
    const updatedAt = parsed.updatedAt ?? parsed.createdAt ?? 0;
    if (!updatedAt || Date.now() - updatedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(DRAFT_KEY);
      return null;
    }
    return parsed;
  } catch {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {}
    return null;
  }
}

export async function clearVideoComposerDraft(): Promise<void> {
  const draft = loadVideoComposerDraft();
  if (isBrowser()) {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {}
  }
  const keys = [
    draft?.pendingAudioFile?.key,
    ...(draft?.pendingReferenceFiles ?? []).map((file) => file.key),
  ].filter(Boolean) as string[];
  await Promise.all(keys.map((key) => deletePendingVideoComposerFile(key)));
}

export async function savePendingVideoComposerFile(
  key: string,
  file: File,
): Promise<VideoComposerPendingFile> {
  const meta: VideoComposerPendingFile = {
    key,
    name: file.name,
    type: file.type,
    size: file.size,
    lastModified: file.lastModified,
  };
  if (!isBrowser() || typeof indexedDB === "undefined") {
    throw new Error("Your browser could not store this file before sign-in.");
  }
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put({
      ...meta,
      file,
      updatedAt: Date.now(),
    } satisfies PendingFileRecord);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("File storage failed"));
    tx.onabort = () => reject(tx.error ?? new Error("File storage aborted"));
  }).finally(() => db.close());
  return meta;
}

export async function loadPendingVideoComposerFile(
  key: string,
): Promise<File | null> {
  if (!isBrowser() || typeof indexedDB === "undefined") return null;
  const db = await openDb();
  const record = await new Promise<PendingFileRecord | undefined>(
    (resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const request = tx.objectStore(STORE_NAME).get(key);
      request.onsuccess = () =>
        resolve(request.result as PendingFileRecord | undefined);
      request.onerror = () =>
        reject(request.error ?? new Error("File load failed"));
      tx.onabort = () => reject(tx.error ?? new Error("File load aborted"));
    },
  ).finally(() => db.close());

  if (!record?.file) return null;
  if (record.file instanceof File) return record.file;
  return new File([record.file], record.name, {
    type: record.type,
    lastModified: record.lastModified,
  });
}

export async function deletePendingVideoComposerFile(
  key: string,
): Promise<void> {
  if (!isBrowser() || typeof indexedDB === "undefined") return;
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("File delete failed"));
    tx.onabort = () => reject(tx.error ?? new Error("File delete aborted"));
  }).finally(() => db.close());
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("Draft database failed"));
  });
}
